import { c as _c } from "react/compiler-runtime";
import { useId } from 'react';
import { CircleHelp, Info } from 'lucide-react';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { RadioGroupItem } from '../ui/radio-group';
export interface RadioGroupItemProps<T extends string> {
  children?: React.ReactNode;
  description?: string;
  disabledReason?: string;
  /**
   * The description on demand, behind a "?" that a tap opens as well as a
   * click: the card keeps its title alone, so that a row of cards stays one
   * line of choices rather than a column of paragraphs.
   */
  hasDescriptionOnDemand?: boolean;
  isDisabled?: boolean;
  label: string;
  value: T;
}

/**
 * A choice presented as a card: the control, a title and a description, with
 * the selected card ringed in full-strength ink.
 *
 * `children` are rendered inside the card, under the description — the design
 * tucks the controls belonging to a choice (a target weight, a curve) into the
 * card itself rather than beside it. They sit outside the `<label>`, though:
 * inside it, every stepper button and figure of the card was read out as the
 * name of the radio, and a label may hold one control only.
 *
 * Why a choice is unavailable is written in the card, not in a tooltip: a
 * tooltip opens on a mouse hover, and on a tablet the card was greyed out
 * with nothing to say why.
 */
export const RadioItem = t0 => {
  const $ = _c(47);
  const {
    children,
    description,
    disabledReason,
    hasDescriptionOnDemand: t1,
    isDisabled,
    label,
    value
  } = t0;
  const hasDescriptionOnDemand = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation();
  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = description ? `${id}-description` : null;
  const reasonId = isDisabled && disabledReason ? `${id}-reason` : null;
  const t2 = !isDisabled && "hover:bg-accent/60";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("flex flex-col gap-3.5 rounded-md border border-border bg-card px-4 py-3.5 transition-colors", "has-[[data-state=checked]]:border-ptl-rule-strong has-[[data-state=checked]]:shadow-[inset_0_0_0_1px_var(--ptl-rule-strong)]", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = isDisabled && "cursor-not-allowed";
  let t5;
  if ($[2] !== t4) {
    t5 = cn("flex flex-1 cursor-pointer items-start gap-3 select-none", t4);
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = !!isDisabled;
  let t7;
  if ($[4] !== descriptionId || $[5] !== reasonId) {
    t7 = [descriptionId, reasonId].filter(Boolean).join(" ") || undefined;
    $[4] = descriptionId;
    $[5] = reasonId;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t6 || $[8] !== t7 || $[9] !== titleId || $[10] !== value) {
    t8 = <RadioGroupItem value={value} className="mt-0.5" disabled={t6} aria-labelledby={titleId} aria-describedby={t7} />;
    $[7] = t6;
    $[8] = t7;
    $[9] = titleId;
    $[10] = value;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = isDisabled && "opacity-70";
  let t10;
  if ($[12] !== t9) {
    t10 = cn("flex flex-col gap-[3px]", t9);
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== label || $[15] !== titleId) {
    t11 = <span id={titleId} className="text-[14.5px] font-semibold">{label}</span>;
    $[14] = label;
    $[15] = titleId;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== description || $[18] !== descriptionId || $[19] !== hasDescriptionOnDemand) {
    t12 = description ? <span id={descriptionId ?? undefined} className={cn("text-[13px] leading-[1.5] text-muted-foreground", hasDescriptionOnDemand && "sr-only")}>{description}</span> : null;
    $[17] = description;
    $[18] = descriptionId;
    $[19] = hasDescriptionOnDemand;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t12) {
    t13 = <span className={t10}>{t11}{t12}</span>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13 || $[26] !== t5 || $[27] !== t8) {
    t14 = <label className={t5}>{t8}{t13}</label>;
    $[25] = t13;
    $[26] = t5;
    $[27] = t8;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== description || $[30] !== hasDescriptionOnDemand || $[31] !== label || $[32] !== t) {
    t15 = description && hasDescriptionOnDemand ? <Popover><PopoverTrigger aria-label={t("About \u201C{{label}}\u201D", {
        label
      })} className="-mt-1 -mr-1.5 inline-flex size-8 shrink-0 items-center justify-center rounded-full text-ptl-ink-3 transition-colors hover:bg-accent hover:text-foreground"><CircleHelp aria-hidden="true" className="size-4" /></PopoverTrigger><PopoverContent align="end" className="text-[13px] leading-[1.5] text-muted-foreground">{description}</PopoverContent></Popover> : null;
    $[29] = description;
    $[30] = hasDescriptionOnDemand;
    $[31] = label;
    $[32] = t;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t14 || $[35] !== t15) {
    t16 = <div className="flex items-start gap-2">{t14}{t15}</div>;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== disabledReason || $[38] !== reasonId) {
    t17 = reasonId ? <p id={reasonId} className="m-0 flex items-start gap-2 pl-7 text-[12.5px] leading-[1.5] text-muted-foreground"><Info aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />{disabledReason}</p> : null;
    $[37] = disabledReason;
    $[38] = reasonId;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== children) {
    t18 = children ? <div className="pl-7">{children}</div> : null;
    $[40] = children;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t16 || $[43] !== t17 || $[44] !== t18 || $[45] !== t3) {
    t19 = <div className={t3}>{t16}{t17}{t18}</div>;
    $[42] = t16;
    $[43] = t17;
    $[44] = t18;
    $[45] = t3;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  return t19;
};